import React, { useEffect, useState, useMemo } from 'react';
import { TextField, DateField, useRedirect, useDataProvider, useVersion } from 'react-admin';
import Card from '@material-ui/core/Card';
import { makeStyles } from '@material-ui/core';
import { keyBy } from 'lodash';

import { Datagrid } from 'components/list';
import Pagination from 'components/pagination/Pagination';
import { ActionField, PriceField } from 'components/fields';

import EnableUserAction from './EnableUserAction';
import BypassFraudAction from './BypassFraudAction';
import ReviewAction from './ReviewAction';
import PhoneField from './PhoneField';
import SuspendAsFraudAction from './SuspendAsFraudAction';
import NameField from './NameField';
import InfoField from './InfoField';

const useStyles = makeStyles((theme) => ({
  expandHeader: {
    width: 'auto',
  },
  expand: {
    backgroundColor: theme.palette.background.default,
  },
  expandCell: {
    padding: '16px 0',
  },
}));

const ExpandedArea = (props) => {
  const { record } = props;
  const dataProvider = useDataProvider();

  const [currentTotal, setCurrentTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [size, setSize] = useState<number>(5);
  const [users, setUsers] = useState<any>([]);
  const [loaded, setLoaded] = useState<boolean>(false);

  const redirect = useRedirect();

  useEffect(() => {
    const getData = async () => {
      try {
        setLoaded(false);
        const start = (page - 1) * size;
        const end = (page - 1) * size + size;

        const ids = record.users
          .slice(start, end)
          .map(({ id }) => id)
          .join(',');

        const { data } = await dataProvider.get('sharedDataUsersDetails', {
          ids,
          page,
          size,
        });

        setUsers(data);
      } finally {
        setLoaded(true);
      }
    };

    getData();
  }, [dataProvider, record.users, page, size]);

  const data = useMemo(() => {
    const start = (page - 1) * size;
    const end = (page - 1) * size + size;

    const paginatedUsers = record.users.slice(start, end).map((user) => ({
      ...user,
      ...(users.find((item) => item.id === user.id) ?? {}),
    }));

    return keyBy(paginatedUsers, ({ id, warehouseId }) => `${id}-${warehouseId}`);
  }, [page, size, record.users, users]);

  const ids = Object.entries(data).map(([id]) => id);

  useEffect(() => {
    setCurrentTotal(record.users.length);
  }, [record.users]);

  const updatePerPage = (newPerPage) => {
    if (page > Math.ceil(currentTotal / newPerPage)) {
      setPage(1);
    }
    setSize(newPerPage);
  };

  return (
    <>
      <Datagrid
        loaded={loaded}
        data={data}
        ids={ids}
        resource="sharedWarehouses"
        currentSort={{ field: 'id' }}
        rowClick={(_id, _basePath, record) => {
          redirect('show', '/users', record.id);
        }}>
        <NameField nameSource="userName" deletedSource="warehouseDeletedAt" label="Name" />
        <InfoField label="" />
        <PhoneField label="Phone" />
        <TextField source="email" label="Email" />
        <DateField
          source="createdAt"
          label="Registered"
          options={{
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
          }}
        />
        <TextField source="shipments" label="Shipments" />
        <PriceField source="totalSpentAmount" label="Spend" />
        <TextField source="warehouseName" label="Warehouse" />
        <ActionField>
          <EnableUserAction />
          <BypassFraudAction />
          <SuspendAsFraudAction />
        </ActionField>
      </Datagrid>
      <Pagination
        page={page}
        setPage={setPage}
        perPage={size}
        setPerPage={updatePerPage}
        total={currentTotal}
      />
    </>
  );
};

const SharedWarehousesList = (props) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const version = useVersion();

  const [warehouses, setWarehouses] = useState<any>([]);
  const [currentTotal, setCurrentTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [loaded, setLoaded] = useState<boolean>(false);

  useEffect(() => {
    const getWarehouses = async () => {
      const { data, total } = await dataProvider.getList('sharedWarehouses', {
        pagination: { page, perPage },
      });

      setCurrentTotal(total);
      setWarehouses(data);
      setLoaded(true);
    };

    getWarehouses();
  }, [dataProvider, page, perPage, version]);

  const updatePerPage = (newPerPage) => {
    if (page > Math.ceil(currentTotal / newPerPage)) {
      setPage(1);
    }
    setPerPage(newPerPage);
  };

  return (
    <Card>
      <Datagrid
        loaded={loaded}
        expand={<ExpandedArea />}
        isRowExpandable
        rowClick="expand"
        classes={{
          expandHeader: classes.expandHeader,
          expandRoot: classes.expand,
          expandCell: classes.expandCell,
        }}
        showExpandButton={false}
        data={keyBy(warehouses, 'id')}
        ids={warehouses.map(({ id }) => id)}
        resource="sharedWarehouses"
        currentSort={{ field: 'id', order: 'DESC' }}>
        <TextField source="warehouseAddress.addressLine" label="From Address" />
        <TextField source="warehouseAddress.postalCode" label="ZIP" />
        <ReviewAction
          source="users.length"
          label="Users"
          textAlign="right"
          apiResource="warehouseAddress"
        />
      </Datagrid>
      <Pagination
        page={page}
        setPage={setPage}
        perPage={perPage}
        setPerPage={updatePerPage}
        total={currentTotal}
      />
    </Card>
  );
};

export default SharedWarehousesList;
